import React, { useEffect, useState } from 'react';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import { useTheme } from '@mui/material/styles';
import { useResponsive } from 'src/hooks/use-responsive';
import Iconify from 'src/components/iconify';
import { LoSam } from 'src/types/losam';
import { OptionModel } from 'src/types/api-response';
import { useLoSam } from 'src/sections/losam/hooks/use-losam';
import { useAuthContext } from 'src/auth/context/jwt';
import { LoSamPosition } from './hooks/use-tracu';
import useCaySamFilterOptions from '../../utils/use-caysam-filter-options';
import CaySamEditDrawer from './CaySamEditDrawer';
import CaySamNewDrawer from './CaySamNewDrawer';
import GinsengCellInfoMini from './components/GinsengCellInfoMini';


type Props = {
  lotName: string;
  selectedLot: LoSam | null;
  gridPositions: LoSamPosition[][];
  gridSize: { x: number; y: number };
  caySamsLoading: boolean;
  onCellClick: (x: number, y: number) => void;
  getGinsengInfo: (x: number, y: number) => any;
  highlightStatus: number;
  highlightHealth: number;
  highlightAge: number;
  excludeLoggedThisMonth?: boolean;
  onDeselectGinseng?: (x: number, y: number) => void;
  deselectedGinsengs?: Set<string>;
  onOpenDrawer?: (caySamData: any, position: { x: number; y: number }) => void;
  loaiTuoiOptions: OptionModel[];
  diemSucKhoeOptions?: OptionModel[];
  onUpdateCaySam?: (caySamData: Partial<any>) => void;
  onSaveCaySam?: () => void;
  onDeleteCaySam?: () => void;
};

export default function LotGridMini({ lotName, selectedLot, gridPositions, gridSize, caySamsLoading, onCellClick, getGinsengInfo, highlightStatus, highlightHealth, highlightAge, excludeLoggedThisMonth = true, onDeselectGinseng, deselectedGinsengs, onOpenDrawer, loaiTuoiOptions, diemSucKhoeOptions, onUpdateCaySam, onSaveCaySam, onDeleteCaySam }: Props) {
  const theme = useTheme();
  const isMobile = useResponsive('down', 'md');
  const { tinhTrangOptions, tuoicayOptions } = useCaySamFilterOptions();
  const { GetCaySamByUser } = useLoSam();
  
  // State for drawer management
  const [editDrawerOpen, setEditDrawerOpen] = React.useState(false);
  const [newDrawerOpen, setNewDrawerOpen] = React.useState(false);
  const [selectedCaySam, setSelectedCaySam] = React.useState<any>(null);
  const [selectedPosition, setSelectedPosition] = React.useState<{ x: number; y: number } | null>(null);
  const [caySamData, setCaySamData] = React.useState<any>(null);
  const { isAdmin } = useAuthContext();

  if (gridPositions.length === 0) return null;
  let isOwnedByUser = true;

  const middleColumn = Math.floor(gridSize.x / 2);
  const getColumnLabel = (x: number) => {
    if (x === middleColumn) return '';
    const adjustedX = x > middleColumn ? x - 1 : x;
    return String.fromCharCode(65 + adjustedX);
  };
  const getRowLabel = (y: number) => (y + 1).toString();

  // Helper functions for drawer management
  const handleOpenEditDrawer = (caySam: any, position: { x: number; y: number }) => {
    setSelectedCaySam(caySam);
    setSelectedPosition(position);
    setCaySamData(caySam);
    setEditDrawerOpen(true);
  };

  const handleOpenNewDrawer = (position: { x: number; y: number }) => {
    setSelectedPosition(position);
    const newCaySamData = {
      loSam_ID: selectedLot?.loSam_ID || 0,
      viTriTrongLo: `${getColumnLabel(position.x)}${getRowLabel(position.y)}`,
      blockChain: null,
      maCaySam: '',
      tuoiCay_ID: 1,
      caySamNhatKys: []
    };
    setCaySamData(newCaySamData);
    setNewDrawerOpen(true);
  };
  
  const handleCloseEditDrawer = () => {
    setEditDrawerOpen(false);
    setSelectedCaySam(null);
    setSelectedPosition(null);
    setCaySamData(null);
  };

  const handleCloseNewDrawer = () => {
    setNewDrawerOpen(false);
    setSelectedPosition(null);
    setCaySamData(null);
  };

  const handleUpdateCaySam = (updatedData: Partial<any>) => {
    setCaySamData((prev: any) => ({ ...prev, ...updatedData }));
    if (onUpdateCaySam) {
      onUpdateCaySam(updatedData);
    }
  };

  const handleDeleteCaySam = () => {
    if (onDeleteCaySam) {
      onDeleteCaySam();
    }
    handleCloseEditDrawer();
  };

  const handleSaveNewCaySam = () => {
    if (onSaveCaySam) {
      onSaveCaySam();
    }
    handleCloseNewDrawer();
  };

  const getStatusColorBackground = (status: number) => {
    switch (status) {
      case 1:
        return theme.palette.success.lighter;
      case 2:
        return theme.palette.blue[200];
      case 3:
        return theme.palette.error.lighter;
      default:
        return theme.palette.grey[50];
    }
  };

  return (
    <Paper sx={{ 
      p: { xs: 1, md: 0 }, 
      mt: 1, 
      height: '100vh', 
      overflow: 'hidden', 
      display: 'flex', 
      flexDirection: 'column' 
    }}>
      {/* <Typography variant="h6" sx={{  
        mb: 1, 
        fontSize: { xs: '0.9rem', md: '1.1rem' }, 
        flexShrink: 0 
      }}>
        Bản đồ lô: {lotName} ({selectedLot ? `${selectedLot.soCot} x ${selectedLot.soHang}` : `${gridSize.x - 1} x ${gridSize.y}`})
        {caySamsLoading && (
          <CircularProgress size={16} sx={{ ml: 2 }} />
        )}
      </Typography> */}

      <Box sx={{ 
        flex: 1, 
        overflow: 'hidden', 
        display: 'flex', 
        flexDirection: 'column',
        width: '100%',
        height: '100%'
      }}>
        {/* Column Headers */}
        <Box sx={{ 
          display: 'flex', 
          mb: 0.5, 
          flexShrink: 0,
          alignItems: 'stretch',
          height: 'clamp(18px, 3vh, 30px)'
        }}>
          {/* Empty space for row labels */}
          <Box sx={{ 
            width: 'clamp(18px, 3vh, 30px)',
            flexShrink: 0
          }} />
          
          {Array.from({ length: gridSize.x }, (_, x) => (
            <Box key={x} sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 'clamp(8px, 1.2vh, 14px)',
              fontWeight: 'bold',
              color: x === middleColumn ? 'transparent' : 'text.primary'
            }}>
              {getColumnLabel(x)}
            </Box>
          ))}
        </Box>

        {/* Grid Container */}
        <Box sx={{ 
          flex: 1,
          display: 'grid',
          gridTemplateRows: `repeat(${gridSize.y}, 1fr)`,
          gap: '3px',
          overflow: 'hidden'
        }}>
          {gridPositions.map((row, rowIndex) => (
            <Box key={rowIndex} sx={{ 
              display: 'grid',
              gridTemplateColumns: `clamp(18px, 3vh, 30px) repeat(${gridSize.x}, 1fr)`,
              gap: '3px',
              alignItems: 'stretch'
            }}>
              {/* Row Label */}
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 'clamp(8px, 1.2vh, 14px)',
                fontWeight: 'bold',
                color: 'text.primary',
                flexShrink: 0
              }}>
                {getRowLabel(rowIndex)}
              </Box>

              {row.map((position) => {
                const isWalkway = position.x === middleColumn;
                const ginsengInfo = getGinsengInfo(position.x, position.y);
                const filterActive = Boolean(highlightStatus || highlightHealth || highlightAge);
                const positionKey = `${position.x}-${position.y}`;
                const isDeselected = deselectedGinsengs?.has(positionKey) || false;
                
                const hasLogThisMonth = (() => {
                  if (!position.caySam?.caySamNhatKys || position.caySam.caySamNhatKys.length === 0) return false;
                  const now = new Date();
                  return position.caySam.caySamNhatKys.some((nk: any) => {
                    const d = new Date(nk?.ngayGhi as any);
                    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
                  });
                })();

                const isHighlightMatch = !!ginsengInfo && !isDeselected && (
                  (highlightStatus ? ginsengInfo.tinhTrang === highlightStatus : true) &&
                  (highlightHealth ? ginsengInfo.diemSucKhoe === highlightHealth : true) &&
                  (highlightAge ? String(ginsengInfo.tuoiCay_ID) === String(highlightAge) : true) &&
                  !(excludeLoggedThisMonth && hasLogThisMonth)
                );

                const getCellColors = () => {
                  if (isWalkway) {
                    return { borderColor: 'transparent', backgroundColor: 'transparent', textColor: 'transparent', hoverColor: 'transparent' };
                  }

                  if (filterActive) {
                    if (isDeselected) {
                      return { borderColor: 'grey.300', backgroundColor: 'grey.50', textColor: 'text.secondary', hoverColor: 'grey.200' };
                    }
                    if (isHighlightMatch) {
                      return { borderColor: 'success.main', backgroundColor: 'success.lighter', textColor: 'success.dark', hoverColor: 'success.main' };
                    }
                    return { borderColor: 'grey.300', backgroundColor: 'grey.100', textColor: 'grey.500', hoverColor: 'grey.200' };
                  }

                  if (position.selected) {
                    return { borderColor: 'primary.main', backgroundColor: 'primary.lighter', textColor: 'primary.main', hoverColor: 'primary.main' };
                  }
                  return { borderColor: 'grey.300', backgroundColor: 'grey.50', textColor: 'text.secondary', hoverColor: 'grey.200' };
                };

                const colors = getCellColors();

                const tooltipHeaderText = (() => {
                  if (filterActive) {
                    if (isDeselected) return 'Cây sâm - Nhấn để chọn lại';
                    if (isHighlightMatch) return 'Cây sâm - Nhấn để bỏ chọn khỏi kết quả lọc';
                    return 'Thông tin cây sâm';
                  }
                  return 'Thông tin cây sâm';
                })();

                const lastTinhTrang = position.caySam?.caySamNhatKys && position.caySam.caySamNhatKys.length > 0
                  ? position.caySam.caySamNhatKys[position.caySam.caySamNhatKys.length - 1]?.tinhTrang
                  : 0;
                  
                if (!isAdmin) {
                  isOwnedByUser = GetCaySamByUser.some(item => item.caySamId === position.caySam?.caySam_ID) || false;
                }

                const handleCellClick = () => {
                  if (isWalkway) return;
                  const canToggleDeselect = filterActive && !!ginsengInfo && onDeselectGinseng;
                  if (canToggleDeselect) {
                    onDeselectGinseng(position.x, position.y);
                    return;
                  }
                  if (position.occupied && position.caySam) {
                    handleOpenEditDrawer(position.caySam, { x: position.x, y: position.y });
                    return;
                  }
                  if (!position.occupied) {
                    handleOpenNewDrawer({ x: position.x, y: position.y });
                    return;
                  }
                  onCellClick(position.x, position.y);
                };

                const hasLogThisMonthForIcon = (() => {
                  const list = position.caySam?.caySamNhatKys || [];
                  if (!list || list.length === 0) return false;
                  const now = new Date();
                  return list.some((nk: any) => {
                    const d = new Date(nk?.ngayGhi as any);
                    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
                  });
                })();

                return (
                  <Box
                    key={`${position.x}-${position.y}`}
                    onClick={isAdmin || isOwnedByUser ? handleCellClick : undefined}
                    sx={{
                      border: '1px solid',
                      borderColor: colors.borderColor,
                      backgroundColor: isAdmin || isOwnedByUser ? getStatusColorBackground(position.occupied ? lastTinhTrang ?? 0 : 0) : theme.palette.grey[200],
                      cursor: isWalkway || (position.occupied && !isOwnedByUser) ? 'default' : 'pointer',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 'clamp(7px, 1vh, 11px)',
                      fontWeight: 'bold',
                      color: (position.occupied && isOwnedByUser) ? theme.palette.text.primary : theme.palette.text.disabled,
                      '&:hover': { 
                        backgroundColor: (position.occupied && isOwnedByUser) ? theme.palette.success.light : colors.hoverColor,
                        transform: 'scale(1.03)'
                      },
                      transition: 'all 0.2s',
                      position: 'relative',
                      borderRadius: 1,
                      minHeight: 0
                    }}
                  >
                    {hasLogThisMonthForIcon && (
                      <Iconify 
                        icon="solar:check-circle-bold" 
                        sx={{ 
                          position: 'absolute', 
                          top: 2, 
                          right: 2, 
                          color: 'success.main',
                          width: 'clamp(8px, 1.5vh, 12px)',
                          height: 'clamp(8px, 1.5vh, 12px)'
                        }} 
                      />
                    )}
                    {(() => {
                      if (isWalkway) {
                        return (
                          <Box
                            sx={{
                              position: 'absolute',
                              top: '50%',
                              left: '50%',
                              transform: 'translate(-50%, -50%)',
                              width: '80%',
                              height: '2px',
                              backgroundColor: 'grey.400',
                              borderRadius: '2px',
                            }}
                          />
                        );
                      }
                      if (position.occupied && ginsengInfo) {
                        return (
                          <Tooltip title={<Box>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>{tooltipHeaderText}</Typography>
                            <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>Vị trí: {getColumnLabel(position.x)}{getRowLabel(position.y)}</Typography>
                            <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>Vị trí trong lô: {position.caySam?.viTriTrongLo}</Typography>
                            {filterActive && isHighlightMatch && !isDeselected && (
                              <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'warning.main', fontWeight: 'bold' }}>
                                ⚠️ Đang được lọc - Nhấn để bỏ chọn
                              </Typography>
                            )}
                          </Box>} arrow placement="top">
                            <Box sx={{ 
                              textAlign: 'center', 
                              p: 0.25, 
                              height: '100%', 
                              width: '100%',
                              display: 'flex', 
                              flexDirection: 'column', 
                              justifyContent: 'center' 
                            }}>
                              <GinsengCellInfoMini
                                positionLabel={`${getColumnLabel(position.x)}${getRowLabel(position.y)}`}
                                caySam={position.caySam || null}
                                latestNhatKy={position.caySam?.caySamNhatKys && position.caySam.caySamNhatKys.length > 0
                                  ? position.caySam.caySamNhatKys[position.caySam.caySamNhatKys.length - 1] as any
                                  : null}
                                tuoiOptions={tuoicayOptions}
                                tinhTrangOptions={tinhTrangOptions}
                                size="xs"
                                center
                              />
                            </Box>
                          </Tooltip>
                        );
                      }
                      return (
                        <Tooltip title={<Box>
                          <Typography variant="subtitle2" sx={{ mb: 1 }}>Vị trí trống - Nhấn để thêm cây sâm</Typography>
                          <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>Vị trí: {getColumnLabel(position.x)}{getRowLabel(position.y)}</Typography>
                          <Typography variant="caption" sx={{ display: 'block' }}>Trạng thái: Có thể trồng cây sâm mới</Typography>
                        </Box>} arrow placement="top">
                          <Box sx={{ 
                            textAlign: 'center', 
                            height: '100%', 
                            display: 'flex', 
                            flexDirection: 'column', 
                            justifyContent: 'center' 
                          }}>
                            <Typography variant="caption" sx={{
                              fontWeight: 'bold',
                              display: 'block',
                              mb: 0.5,
                              fontSize: 'clamp(6px, 0.9vh, 10px)'
                            }}>
                              {getColumnLabel(position.x)}{getRowLabel(position.y)}
                            </Typography>
                            <Iconify 
                              icon="solar:add-circle-bold" 
                              sx={{ 
                                color: 'grey.400',
                                width: 'clamp(10px, 1.8vh, 16px)',
                                height: 'clamp(10px, 1.8vh, 16px)'
                              }} 
                            />
                            <Typography variant="caption" sx={{
                              display: 'block',
                              mt: 0.25,
                              fontSize: 'clamp(6px, 0.8vh, 9px)'
                            }}>
                              Trống
                            </Typography>
                          </Box>
                        </Tooltip>
                      );
                    })()}
                  </Box>
                );
              })}
            </Box>
          ))}
        </Box>
      </Box>

      {/* Edit Drawer */}
      {selectedCaySam && selectedPosition && (
        <CaySamEditDrawer
          open={editDrawerOpen}
          onClose={handleCloseEditDrawer}
          title={`Cập nhật cây sâm - ${getColumnLabel(selectedPosition.x)}${getRowLabel(selectedPosition.y)}`}
          lotName={lotName}
          lotCode={selectedLot?.maLo || ''}
          gardenName={selectedLot?.vuonTrongs?.[0]?.tenVuonTrong || ''}
          positionLabel={`${getColumnLabel(selectedPosition.x)}${getRowLabel(selectedPosition.y)}`}
          caySamData={caySamData}
          onUpdateCaySam={handleUpdateCaySam}
          loaiTuoiOptions={loaiTuoiOptions}
          diemSucKhoeOptions={diemSucKhoeOptions}
          loading={caySamsLoading} 
        />
      )}

      {/* New Drawer */}
      {selectedPosition && !selectedCaySam && (
        <CaySamNewDrawer
          open={newDrawerOpen}
          onClose={handleCloseNewDrawer}
          title={`Thêm cây sâm mới - ${getColumnLabel(selectedPosition.x)}${getRowLabel(selectedPosition.y)}`}
          lotName={lotName}
          lotCode={selectedLot?.maLo || ''}
          gardenName={selectedLot?.vuonTrongs?.[0]?.tenVuonTrong || ''}
          positionLabel={`${getColumnLabel(selectedPosition.x)}${getRowLabel(selectedPosition.y)}`}
          caySamData={caySamData}
          onUpdateCaySam={handleUpdateCaySam}
          loaiTuoiOptions={loaiTuoiOptions}
          diemSucKhoeOptions={diemSucKhoeOptions}
          loading={caySamsLoading}
          onSave={handleSaveNewCaySam}
        />
      )}
    </Paper>
  );
}